/**
 * Le dictionnaire français — et la **forme** que l'anglais doit épouser.
 *
 * `type Textes = typeof FR` : c'est ce fichier qui définit les clés, les arités et
 * les types des paramètres. Une clé ajoutée ici sans son pendant anglais ne compile
 * pas, et c'est le filet le plus solide qu'on puisse tendre — il ne coûte rien et il
 * ne s'oublie pas.
 *
 * ## Ce qu'on ne traduit pas
 *
 * Zinc, nénette, rampeau, cave, recave, sec, 421 : c'est le **nom des choses**. Un
 * joueur anglais qui apprend le 421 apprend « le sec » ; « the dry throw » n'existe
 * pas et ne veut rien dire. Ces entrées-là sont identiques dans les deux langues, et
 * `VOCABULAIRE_COMMUN` les déclare nommément — pour qu'une exception soit un geste
 * délibéré et visible, jamais un oubli.
 *
 * ## Pourquoi des fonctions plutôt que des trous à remplir
 *
 * `« %s jeton(s) »` oblige à un moteur de pluriel et laisse le compilateur aveugle.
 * Une fonction, elle, porte ses paramètres dans son type : impossible d'oublier un
 * argument, impossible d'en passer un de trop, et chaque langue accorde à sa façon —
 * le français fait « 1 jeton / 3 jetons », d'autres langues font autrement.
 */

/** Un lieu de la tournée : son nom et sa ligne d'ambiance. */
interface Lieu {
  readonly nom: string;
  readonly accroche: string;
}

const COMPTOIRS: Readonly<Record<string, Lieu>> = {
  gare: {
    nom: "Le Café de la Gare",
    accroche: "Un client pressé, un train à prendre. Il lance et il pose.",
  },
  balto: {
    nom: "Le Balto",
    accroche: "Deux habitués, un percolateur, et personne qui s'arrête jamais.",
  },
  petitZinc: {
    nom: "Le Petit Zinc",
    accroche: "On y joue distraitement, entre deux commandes. À toi d'en profiter.",
  },
  chope: {
    nom: "La Chope",
    accroche: "Quatre autour du zinc : trois occasions de faire pire que toi.",
  },
  tabac: {
    nom: "Le Tabac des Sports",
    accroche: "Le flambeur court après le 421. Cours-y avant lui.",
  },
  cercle: {
    nom: "Le Cercle",
    accroche: "Ici on compte. Cinq à table, et un calculateur qui s'arrête à temps.",
  },
  arriereSalle: {
    nom: "L'Arrière-salle",
    accroche: "Le patron joue de mémoire, et il commente. Ne l'écoute pas.",
  },
  taulier: {
    nom: "Le Comptoir du Taulier",
    accroche: "La cave, la recave, la cave des caves. Une seule soirée, et c'est lui.",
  },
};

/** Les huit adversaires, indexés par la clé stable partagée avec le simulateur. */
const ADVERSAIRES: Readonly<Record<string, string>> = {
  presse: "Le pressé",
  acharne: "L'acharné",
  distrait: "Le distrait",
  habitue: "L'habitué",
  flambeur: "Le flambeur",
  calculateur: "Le calculateur",
  patron: "Le patron",
  taulier: "Le taulier",
};

/** Rendu d'une clé de contenu inconnue — visible, jamais silencieux. */
function manquant(cle: string): string {
  return `« ? ${cle} »`;
}

export const FR = {
  // ------------------------------------------------------------- le vocabulaire
  appNom: "Zinc 421",
  motSec: "SEC",
  motRampeau: "Rampeau",
  motNenette: "nénette",
  etapeCave: "La cave",
  etapeRecave: "La recave",
  etapeFinale: "La cave des caves",
  etapeFini: "Terminé",

  // --------------------------------------------------------------- les nombres
  jetons: (n: number) => `${n} jeton${n > 1 ? "s" : ""}`,
  des: (n: number) => `${n} dé${n > 1 ? "s" : ""}`,
  lancers: (n: number) => `${n} lancer${n > 1 ? "s" : ""}`,
  manches: (n: number) => `${n} manche${n > 1 ? "s" : ""}`,
  tentatives: (n: number) => `${n} tentative${n > 1 ? "s" : ""}`,

  // ------------------------------------------------------------------ contenu
  comptoirNom: (cle: string) => COMPTOIRS[cle]?.nom ?? manquant(cle),
  comptoirAccroche: (cle: string) => COMPTOIRS[cle]?.accroche ?? manquant(cle),
  adversaireNom: (cle: string) => ADVERSAIRES[cle] ?? manquant(cle),
  /** « au Balto », « à la Chope » : la contraction est une affaire de langue. */
  auComptoir: (lieu: string) => {
    if (lieu.startsWith("Le ")) return `au ${lieu.slice(3)}`;
    if (lieu.startsWith("La ")) return `à la ${lieu.slice(3)}`;
    if (lieu.startsWith("L'") || lieu.startsWith("L’")) return `à l’${lieu.slice(2)}`;
    if (lieu.startsWith("Les ")) return `aux ${lieu.slice(4)}`;
    return `à ${lieu}`;
  },

  // ------------------------------------------------------------ combinaisons
  comboQuatreCentVingtEtUn: "421 !",
  comboBrelanAs: "brelan d'as",
  comboBrelan: (hauteur: number) => `brelan de ${hauteur}`,
  comboPaireAs: "paire d'as",
  comboNenette: "nénette",
  comboSuite: "suite",

  // ---------------------------------------------------------------- l'accueil
  accueilAccroche: "Le 421 du comptoir. Le sec double la mise.",
  combienATable: "Combien à table ?",
  quiJoue: "Qui joue ?",
  adversairesVirtuels: (n: number) =>
    `${n} adversaire${n > 1 ? "s" : ""} virtuel${n > 1 ? "s" : ""}`,
  tableToutHumaine: "table entièrement humaine",
  nomDuJoueur: (rang: number) => `Nom du joueur ${rang}`,
  joueurNumero: (rang: number) => `Joueur ${rang}`,
  retirerJoueur: (nom: string) => `Retirer ${nom}`,
  ajouterUnJoueur: "+ un joueur sur ce téléphone",
  enFace: "En face",
  perdTaux: (taux: string) => `perd ${taux}`,
  jouer: "Jouer",
  onPrepareLaTable: "On prépare la table…",
  laTournee: "La tournée des comptoirs",
  jouerEnLigne: "Jouer en ligne, chacun son téléphone",
  justeLancerLesDes: "Juste lancer les dés",
  sonActif: "🔊 Son et vibrations : oui",
  sonCoupe: "🔇 Son et vibrations : non",
  choisirLaLangue: "Langue",
  reprendreLaPartie: "Reprendre la partie",
  repriseSoiree: "Soirée en cours",
  repriseManche: (n: number) => `manche ${n}`,

  // ---------------------------------------------------------------- la partie
  enjeuCharge: "Charge — le plus faible ramasse les jetons de la masse.",
  enjeuDecharge: "Décharge — le plus fort donne ses jetons au plus faible.",
  partieTerminee: "Partie terminée.",
  masse: (jetons: string) => `Masse : ${jetons}`,
  mancheNumero: (n: number) => `manche ${n}`,
  lesJoueurs: "Les joueurs",
  lesTroisDes: "Les trois dés",
  renommerJoueur: (nom: string) => `Renommer ${nom}`,
  nouveauNom: "Nouveau nom",
  valider: "Valider",
  sauve: "sauvé",
  dejaPose: "Déjà posé, du plus fort au plus faible",
  deAria: (rang: number, quoi: string) => `Dé ${rang} : ${quoi}`,
  deEnLAir: "en l'air",
  deGarde: (valeur: number) => `${valeur}, gardé`,
  lancerLesDes: "🎲 Lancer les dés",
  relancerDes: (n: number) => `🎲 Relancer ${n} dé${n > 1 ? "s" : ""}`,
  jeMArrete: "Je m’arrête",
  unConseil: "Un conseil ?",
  demanderConseil: "Demander conseil",
  cEstFini: "C’est fini",
  finDePartie: "Fin de partie",
  paieSaTournee: "paie sa tournée",
  quitter: "Quitter",
  couperLeSon: "Couper le son",
  remettreLeSon: "Remettre le son",
  qualiteReduite: "Qualité réduite (fps bas)",
  contextePerdu: "L’affichage 3D a été interrompu par le système. La partie est "
    + "intacte ; la scène revient dès que possible.",

  // --------------------------------------------------------------- la consigne
  desQuiRoulent: "Les dés roulent…",
  paieSaTourneePhrase: (nom: string) => `${nom} paie sa tournée.`,
  aQui: (nom: string, suite: string) => `${nom} — ${suite}`,
  rampeauLance: (nom: string) => `Rampeau — ${nom} lance.`,
  rampeauUnLancer: "rampeau, un seul lancer pour départager.",
  ilReflechit: (nom: string) => `${nom} réfléchit…`,
  aToiDOuvrir: "à toi d'ouvrir. T'arrêter maintenant ne laissera qu'un lancer à "
    + "tout le monde.",
  aToi: (suite: string) => `à toi. ${suite}`,
  mancheSeche: "La manche est sèche : un seul lancer.",
  lancersAuMaximum: (n: number) => `${n} lancers au maximum.`,
  plusDeRelance: "plus de relance : tu poses ce que tu as.",
  gardeOuRelanceFixe: (n: number) =>
    `garde ce qui te va, ou relance. T'arrêter fixerait la manche à ${n} lancer`
    + `${n > 1 ? "s" : ""}.`,
  gardeOuRelanceReste: (n: number) =>
    `garde ce qui te va, ou relance — il te reste ${n} lancer${n > 1 ? "s" : ""}.`,

  // ------------------------------------------------------------- le passage
  passerLAppareil: "Passe l’appareil",
  passerLAppareilAria: "Passer l’appareil",
  cEstMoi: "C’est moi",

  // ------------------------------------------------------------- les annonces
  annonce421: (nom: string, moi: boolean) =>
    (moi ? "421 ! Tu rafles la mise." : `421 ! ${nom} rafle la mise.`),
  annonceNenette: (nom: string, moi: boolean) =>
    (moi ? "La nénette pour toi — la plus faible du jeu."
      : `La nénette pour ${nom} — la plus faible du jeu.`),
  annonceSec: "SEC ! Un seul lancer pour tout le monde, et la mise double.",
  annonceRampeau: (noms: string, enjeu: string) =>
    `Rampeau — ${noms}. Un lancer chacun pour savoir ${enjeu}.`,
  rampeauQuiRamasse: "qui ramasse",
  rampeauQuiDonne: "qui donne",
  rampeauQuiRecoit: "qui reçoit",
  annonceRamasse: (nom: string, jetons: string, moi: boolean) =>
    (moi ? `Tu ramasses ${jetons}.` : `${nom} ramasse ${jetons}.`),
  miseDoublee: "La mise était doublée : la manche était sèche.",
  misePotVide: "C'était tout ce qui restait au pot.",
  annonceDecharge: "DÉCHARGE ! La masse est vide : il faut maintenant se débarrasser "
    + "de ses jetons.",
  annonceSauve: (nom: string, moi: boolean) =>
    (moi ? "Tu es sorti d'affaire." : `${nom} est sorti d'affaire.`),
  annonceFin: (nom: string, moi: boolean) =>
    (moi ? "Tu paies ta tournée." : `${nom} paie sa tournée.`),

  // ----------------------------------------------------------------- le coach
  coachPoser: (main: string) => `Plus de lancer : tu poses ${main}.`,
  coachArreter: (main: string) => `Arrête-toi : ${main} suffit.`,
  coachToutRelancer: (main: string) => `Relance tout, ${main} ne vaut rien.`,
  coachGarder: (gardes: string, des: string) => `Garde ${gardes}, relance ${des}.`,
  coachDe: (valeur: number) => `le ${valeur}`,
  coachEtPuis: (debut: string, dernier: string) => `${debut} et ${dernier}`,
  coachEnjeuCharge: "En charge, le plus faible ramasse la mise.",
  coachEnjeuDecharge: "En décharge, il faut être le plus FORT pour donner.",
  coachObjectif: (main: string, sur: number) =>
    `Il faut battre ${main} — un lancer neuf y arrive une fois sur ${sur}.`,
  coachObjectifJamais: (main: string) =>
    `Il faut battre ${main} — un lancer neuf n'y arrive jamais.`,
  coachObjectifToujours: (main: string) =>
    `Il faut battre ${main} — presque tous les lancers y arrivent.`,

  // -------------------------------------------------------------- la campagne
  campagneTitre: "La tournée des comptoirs",
  avancement: (gagnes: number, total: number, etoiles: number) =>
    `${gagnes} comptoir${gagnes > 1 ? "s" : ""} sur ${total}`
    + ` · ${etoiles} étoile${etoiles > 1 ? "s" : ""}`,
  tourneeToutesEtoiles: "Toute la tournée, et toutes les étoiles. Il n'y a plus "
    + "personne à battre.",
  tourneeFinie: (etoiles: number, total: number) =>
    `Toute la tournée est faite — ${etoiles} étoile${etoiles > 1 ? "s" : ""} sur `
    + `${total}. Les défis manquants t'attendent.`,
  tonNomAuComptoir: "Ton nom au comptoir",
  tonNomPlaceholder: "Toi",
  comptoirVerrouille: "Gagne le comptoir précédent pour entrer.",
  comptoirATable: (joueurs: number, adversaires: string) =>
    `${joueurs} à table : ${adversaires}`,
  comptoirSoiree: " · soirée entière : cave, recave, cave des caves",
  comptoirDefi: (defi: string) => `Défi : ${defi}`,
  comptoirGagne: "gagné",
  defiReussiAria: "défi réussi",
  depuisLeDebut: "Depuis le début",
  tesStatistiques: "Tes statistiques",
  revenirAuComptoir: "Revenir au comptoir",
  stockageMuet: "Cet appareil refuse d’enregistrer : la tournée ne sera pas retenue.",

  // ------------------------------------------------------------------ les défis
  defiAvec421: "Gagner en réussissant un 421",
  defiAvecSec: "Gagner en séchant au moins une manche",
  defiSansRampeau: "Gagner sans passer par un rampeau",
  defiEnMoinsDe: (manches: number) => `Gagner en ${manches} manches ou moins`,
  defiAuPlus: (jetons: number) => `Gagner en ramassant ${jetons} jetons au plus`,

  // ------------------------------------------------------------------- l'issue
  issueGagneEtoile: (lieu: string) => `★ ${lieu} est à toi, le défi avec.`,
  issueGagne: (lieu: string, defi: string) =>
    `${lieu} est à toi. Défi manqué : ${defi.charAt(0).toLowerCase()}${defi.slice(1)}`,
  issuePerdu: (auLieu: string) => `Tu paies la tournée ${auLieu}. On y retourne ?`,
  retourALaTournee: "Retour à la tournée",
  enchainerLaRecave: "Enchaîner : la recave",
  laSuite: "La suite",
  voirQuiPaie: "Voir qui paie",
  uneAutreSoiree: "Une autre soirée ?",

  // ---------------------------------------------------------- les statistiques
  statParties: "Parties jouées",
  statGagnees: "Gagnées",
  statPourcentage: (valeur: number, pourcent: number) => `${valeur} (${pourcent} %)`,
  statTournees: "Tournées payées",
  statManches: "Manches jouées",
  stat421: "421 réussis",
  statNenettes: "Nénettes encaissées",
  statSecs: "Secs imposés",
  statRampeaux: "Rampeaux disputés",
  statCharge: "Ramassages en charge",
  statDecharge: "Réceptions en décharge",
  statJetons: "Jetons ramassés",
  statMeilleurCoup: "Meilleur coup",

  // ---------------------------------------------------------------- en ligne
  enLigneTitre: "Jouer en ligne",
  enLigneAccroche: "Chacun sur son téléphone, un code à partager.",
  tonNomATable: "Ton nom à table",
  tonNom: "Ton nom",
  ouvrirUnePartie: "Ouvrir une partie",
  onOuvreLaTable: "On ouvre la table…",
  ouRejoindreAvecUnCode: "Ou rejoindre avec un code",
  codeDeLaPartie: "Code de la partie",
  codeRecu: "code reçu",
  rejoindre: "Rejoindre",
  salleDAttente: "Salle d’attente",
  partageCeCode: "Partage ce code, ou complète avec des adversaires.",
  lesJoueursPresents: "Les joueurs présents",
  joueurVirtuel: "virtuel",
  joueurEnLigne: "en ligne",
  completerAvecUnVirtuel: "Compléter avec un adversaire virtuel",
  aLHoteDeLancer: "C’est à celui qui a ouvert la table de lancer la partie.",
  lancerLaPartie: "Lancer la partie",
  joueurDeconnecte: "déconnecté",
  joueurRemplace: "remplacé",
  cEstToi: " (toi)",
  affichageRattrape: " · affichage rattrapé",
  tableNumero: (code: string) => `Table ${code}`,

  niveauNumero: (n: number) => `niveau ${n}`,
  encoreDesJoueurs: (n: number) => `Encore ${n} joueur${n > 1 ? "s" : ""}`,
  commencerLaCave: "Commencer la cave",
  quitterLaTable: "Quitter la table",
  toutRelacher: "Tout relâcher",
  retour: "Retour",
  combinaisonEtJetons: (nom: string, jetons: string) => `${nom} — ${jetons}`,

  // ------------------------------------------------------------- le bac à sable
  bacLanceLesDes: "Lance les trois dés.",
  bacTuGardesTout: "Tu gardes tout — c'est ton résultat.",
  bacToucheUnDe: "Touche un dé pour le garder, puis relance.",
  bacPasEncoreLance: "pas encore lancé",
  bacMoteurNomme: "Le moteur de règles partagé nomme la combinaison",
} ;

export type Textes = typeof FR;

/**
 * Les entrées identiques dans les deux langues, déclarées **nommément**.
 *
 * C'est le nom des choses : on ne traduit pas « le sec », pas plus qu'on ne traduit
 * « yahtzee » ou « gin rummy ». Une règle floue (« tout ce qui contient 421 ») aurait
 * dispensé de traduction des phrases entières le jour où l'une d'elles cite le mot.
 */
export const VOCABULAIRE_COMMUN: ReadonlySet<string> = new Set([
  "appNom",
  "motSec",
  "motRampeau",
  "motNenette",
  "etapeCave",
  "etapeRecave",
  "etapeFinale",
  "comboQuatreCentVingtEtUn",
  "comboNenette",
]);
